/**
 * InputRoundsManager — Facilitator landing page for crowd input rounds.
 * Lists all rounds with status, visibility mode, response rate, and deadline.
 * Provides a "New Round" form to create and configure a round.
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users2,
  Plus,
  Eye,
  EyeOff,
  Clock,
  CheckCircle,
  Lock,
  ChevronRight,
  CalendarDays,
  X,
} from 'lucide-react';
import { PageHeader } from '../../../components/prioritization/PageHeader';
import { useInputRoundsStore } from '../../../stores/prioritization/inputRoundsStore';
import { useUserStore } from '../../../stores/prioritization/userStore';
import type { InputRoundVisibility } from '../../../types/prioritization/inputRounds';

// ============================================================================
// Helpers
// ============================================================================

function statusBadge(status: string) {
  if (status === 'open')
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-[var(--green-dim)] border border-[var(--green)] text-[var(--green)] dark:text-[var(--text-ter)]">
        <span className="w-1.5 h-1.5 rounded-full bg-[var(--green)] inline-block" />
        Open
      </span>
    );
  if (status === 'closed')
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-[var(--hover-tint)] border border-[var(--rule)] text-[var(--text-sec)]">
        <Lock className="h-2.5 w-2.5" />
        Closed
      </span>
    );
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-[var(--slate-dim)] border border-[var(--slate)] text-[var(--text-sec)]">
      <Eye className="h-2.5 w-2.5" />
      Revealed
    </span>
  );
}

// ============================================================================
// New Round Form
// ============================================================================

interface NewRoundFormProps {
  onClose: () => void;
}

function NewRoundForm({ onClose }: NewRoundFormProps) {
  const createRound = useInputRoundsStore((s) => s.createRound);
  const userId = useUserStore((s) => s.userId);
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [visibilityMode, setVisibilityMode] = useState<InputRoundVisibility>('open');
  const [deadline, setDeadline] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const id = await createRound({
        name: name.trim(),
        description: description.trim() || undefined,
        visibilityMode,
        deadline: deadline ? new Date(deadline) : null,
        createdBy: userId,
      });
      navigate(`/prioritization/input-rounds/${id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create round');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-semibold text-[var(--text-pri)]">New Input Round</h3>
        <button type="button" onClick={onClose} className="p-1 text-[var(--text-ter)] hover:text-[var(--text-sec)] rounded">
          <X className="h-4 w-4" />
        </button>
      </div>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Round name *</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Q3 Effort Estimates"
            required
            className="w-full px-3 py-2 border border-[var(--rule)] text-sm focus:ring-2 focus:ring-violet-500 focus:border-[var(--slate)]"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Description</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="What are you asking people to estimate?"
            rows={2}
            className="w-full px-3 py-2 border border-[var(--rule)] text-sm resize-none focus:ring-2 focus:ring-violet-500 focus:border-[var(--slate)]"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-[var(--text-sec)] mb-2">Visibility</label>
          <div className="flex gap-3">
            {(['open', 'blind'] as InputRoundVisibility[]).map((mode) => (
              <button
                key={mode}
                type="button"
                onClick={() => setVisibilityMode(mode)}
                className={`flex-1 flex items-center gap-2 px-3 py-2.5  border text-sm transition-colors ${
                  visibilityMode === mode
                    ? 'bg-[var(--slate-dim)] border-[var(--slate)] text-[var(--text-sec)]'
                    : 'bg-[var(--bg-raised)] border-[var(--rule)] text-[var(--text-sec)] hover:border-[var(--rule)]'
                }`}
              >
                {mode === 'open' ? <Eye className="h-4 w-4 shrink-0" /> : <EyeOff className="h-4 w-4 shrink-0" />}
                <div className="text-left">
                  <div className="font-medium capitalize">{mode}</div>
                  <div className="text-[10px] opacity-70">
                    {mode === 'open' ? "Inputs visible as they come in" : "Hidden until you reveal"}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Deadline (optional)</label>
          <input
            type="datetime-local"
            value={deadline}
            onChange={(e) => setDeadline(e.target.value)}
            className="w-full px-3 py-2 border border-[var(--rule)] text-sm focus:ring-2 focus:ring-violet-500 focus:border-[var(--slate)]"
          />
        </div>
        {error && <p className="text-sm text-[var(--coral)]">{error}</p>}
        <div className="flex gap-3 pt-1">
          <button
            type="submit"
            disabled={loading || !name.trim()}
            className="flex-1 flex items-center justify-center gap-1.5 px-4 py-2 bg-[var(--slate)] text-white text-sm font-medium hover:bg-[var(--slate)] disabled:opacity-50"
          >
            {loading ? 'Creating…' : 'Create round'}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-[var(--text-sec)] hover:bg-[var(--hover-tint)]"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export function InputRoundsManager() {
  const navigate = useNavigate();
  const { rounds, fetchFromSupabase, getResponseRate } = useInputRoundsStore();

  useEffect(() => {
    fetchFromSupabase();
  }, [fetchFromSupabase]);

  const [showForm, setShowForm] = useState(false);

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<Users2 className="h-4 w-4 text-[var(--text-ter)]" />}
        iconBg="bg-[var(--slate-dim)]"
        title="Input Rounds"
        subtitle="Collect structured estimates and opinions from your team on any attribute"
        actions={
          !showForm ? (
            <button
              onClick={() => setShowForm(true)}
              className="btn btn-primary flex items-center gap-2"
            >
              <Plus className="h-4 w-4" />
              New Round
            </button>
          ) : null
        }
      />

      {showForm && <NewRoundForm onClose={() => setShowForm(false)} />}

      {rounds.length === 0 && !showForm ? (
        <div className="text-center py-16 bg-[var(--bg-raised)] border border-[var(--rule)]">
          <Users2 className="h-12 w-12 text-[var(--text-ter)] mx-auto mb-3" />
          <p className="font-medium text-[var(--text-sec)]">No input rounds yet</p>
          <p className="text-sm text-[var(--text-ter)] mt-1">
            Create a round to gather team estimates on item attributes.
          </p>
          <button
            onClick={() => setShowForm(true)}
            className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 bg-[var(--slate)] text-white text-sm font-medium hover:bg-[var(--slate)]"
          >
            <Plus className="h-4 w-4" />
            New Round
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3">
          {rounds.map((round) => {
            const rate = getResponseRate(round.id);
            const isDeadlinePassed = round.deadline && new Date(round.deadline) < new Date();
            return (
              <div
                key={round.id}
                className="bg-[var(--bg-raised)] border border-[var(--rule)] p-4 flex items-center gap-4 hover:border-[var(--slate)] hover:shadow-sm cursor-pointer transition-all"
                onClick={() => navigate(`/prioritization/input-rounds/${round.id}`)}
              >
                {/* Icon */}
                <div className="p-2.5 bg-[var(--slate-dim)] shrink-0">
                  {round.visibilityMode === 'blind'
                    ? <EyeOff className="h-5 w-5 text-[var(--text-ter)]" />
                    : <Eye className="h-5 w-5 text-[var(--text-ter)]" />}
                </div>

                {/* Main info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-[var(--text-pri)] text-sm">{round.name}</span>
                    {statusBadge(round.status)}
                    <span className="text-[10px] text-[var(--text-ter)] capitalize">
                      {round.visibilityMode}
                    </span>
                  </div>
                  {round.description && (
                    <p className="text-xs text-[var(--text-ter)] truncate mt-0.5">{round.description}</p>
                  )}
                  <div className="flex items-center gap-4 mt-1.5">
                    {/* Response rate */}
                    {rate.total > 0 && (
                      <div className="flex items-center gap-1.5 text-xs text-[var(--text-ter)]">
                        <div className="w-16 h-1.5 bg-[var(--hover-tint)] rounded-full overflow-hidden">
                          <div
                            className="h-full bg-[var(--slate)] rounded-full"
                            style={{ width: `${rate.percent}%` }}
                          />
                        </div>
                        <span>{rate.responded}/{rate.total} responded</span>
                      </div>
                    )}
                    {/* Deadline */}
                    {round.deadline && (
                      <div className={`flex items-center gap-1 text-xs ${isDeadlinePassed ? 'text-[var(--coral)]' : 'text-[var(--text-ter)]'}`}>
                        <CalendarDays className="h-3 w-3" />
                        {new Date(round.deadline).toLocaleDateString()}
                      </div>
                    )}
                  </div>
                </div>

                {/* Status icon + chevron */}
                <div className="flex items-center gap-2 shrink-0">
                  {round.status === 'open' && <Clock className="h-4 w-4 text-[var(--green)]" />}
                  {round.status === 'closed' && <Lock className="h-4 w-4 text-[var(--text-ter)]" />}
                  {round.status === 'revealed' && <CheckCircle className="h-4 w-4 text-[var(--text-ter)]" />}
                  <ChevronRight className="h-4 w-4 text-[var(--text-ter)]" />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default InputRoundsManager;
